import { Button, DatePicker, DropdownSelect, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import { useAppSelector } from '@hooks/useRedux';
import { selectAllOutlets } from '@store/outlets';
import React, { useMemo, useState } from 'react';
import { AccountingPeriodFormState } from './accountingPeriods';

const COMPANY_WIDE_OUTLET_VALUE = '__company_wide__';

interface AccountingPeriodFormModalProps {
  initial: AccountingPeriodFormState;
  title: string;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (form: AccountingPeriodFormState) => void;
}

export function AccountingPeriodFormModal({
  initial,
  title,
  isSaving,
  onClose,
  onSubmit,
}: AccountingPeriodFormModalProps) {
  const [form, setForm] = useState<AccountingPeriodFormState>(initial);
  const outlets = useAppSelector(selectAllOutlets);

  const outletOptions = useMemo(
    () => [
      { value: COMPANY_WIDE_OUTLET_VALUE, label: 'Company-wide (all outlets)' },
      ...outlets.map((outlet) => ({ value: outlet.id, label: outlet.name })),
    ],
    [outlets],
  );

  const setField = <K extends keyof AccountingPeriodFormState>(
    key: K,
    value: AccountingPeriodFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-3"
      >
        <FormInput
          label="Period name"
          required
          value={form.name}
          onChange={(event) => setField('name', event.target.value)}
          placeholder="e.g. May 2026"
        />

        <DropdownSelect
          label="Outlet"
          value={form.outletId ?? COMPANY_WIDE_OUTLET_VALUE}
          options={outletOptions}
          onChange={(value) =>
            setField(
              'outletId',
              value === COMPANY_WIDE_OUTLET_VALUE ? null : value,
            )
          }
        />

        <div className="grid gap-3 md:grid-cols-2">
          <DatePicker
            label="Start date"
            mode="single"
            value={form.periodStart}
            onChange={(value) => setField('periodStart', value)}
            placeholder="Select start date"
          />
          <DatePicker
            label="End date"
            mode="single"
            value={form.periodEnd}
            onChange={(value) => setField('periodEnd', value)}
            placeholder="Select end date"
          />
        </div>

        <FormTextarea
          label="Notes"
          value={form.notes}
          onChange={(event) => setField('notes', event.target.value)}
          rows={4}
          placeholder="Optional notes about this period"
        />

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save period
          </Button>
        </div>
      </form>
    </Modal>
  );
}
